import { ChevronDownIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Input,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    Tooltip,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import BuildUrlParamsUpdate from 'components/adspresso/BuildUrlParamsUpdate';
import UrlParametersUpdate from 'components/adspresso/UrlParametersUpdate';
import CatalogueFrameForSingle from 'components/genericForms/CatalogueFrameForSingle';
import CustomDeepLinkInput from 'components/genericForms/CustomDeepLinkInput';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import 'react-phone-input-2/lib/style.css';
import { useSelector } from "react-redux";
import { useCreateAdMutation } from 'store/campaigns/adsApi';
import { useGetAdvertisableAppsQuery } from 'store/campaigns/adsetFormApi';
import { selectSelectedAdset } from 'store/campaigns/campaignsSlice';
import { removeEmptyValues } from 'utils/functions';
import * as Yup from 'yup';

const CreateSingleCatalogueAdForm = ({ onFormValuesChange, adFormik, adsAccountData, onSuccess, onClose, onSave }) => {

    const toast = useToast();
    const background = useColorModeValue('gray.50', 'gray.800');
    const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const selectedAdset = useSelector(selectSelectedAdset);
    const [isUrlModalOpen, setUrlModalOpen] = useState(false);
    const [isUrlParamModalOpen, setUrlParamModalOpen] = useState(false);
    const [prevAdFormikValues, setPrevAdFormikValues] = useState(adFormik.values);
    const { isOpen: isCreativeModalOpen, onOpen: onCreativeModalOpen, onClose: onCreativeModalClose } = useDisclosure();

    const [createAd] = useCreateAdMutation();
    const { data: advertisableApps } = useGetAdvertisableAppsQuery({ adsAccountId: adsAccountData?.account_id }, { skip: !adsAccountData?.account_id });

    const validDynamicTagRegex = /^{{\s*(product|hotel)\.url\s*}}$/;

    const mustStartWithHttpRegex = /^https?:\/\/.+$/;


    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        cpa: Yup.number()
            .typeError('must be a number')
            .positive('must be a positive number'),
        creative: Yup.object().shape({
            object_story_spec: Yup.object().shape({
                template_data: Yup.object().shape({
                    destination_type: Yup.string(),
                    link: Yup.string().required('URL is required'),
                    call_to_action: Yup.object().shape({
                        type: Yup.string().required('Call to action type is required'),
                    }),
                }),
                page_id: Yup.string().required('Facebook page is required')
            }),
            template_url_spec: Yup.object().shape({
                android: Yup.object().shape({
                    url: Yup.string()
                        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
                }),
                ios: Yup.object().shape({
                    url: Yup.string()
                        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
                }),
                web: Yup.object().shape({
                    url: Yup.string()
                        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://')
                }),

            })
        }),
    });

    const formik = useFormik({
        initialValues: {
            name: adFormik.values.name || '',
            hash: '',
            cpa: '',
            adType: 'catalogue',
            creative: {
                name: '',
                url_tags: '',
                applink_treatment: 'web_only',
                app_product_page_id: '',
                template_url_spec: {
                    web: {
                        url: ''
                    },
                    config: {
                        app_id: ''
                    },
                    android: {
                        url: ''
                    },
                    ios: {
                        url: ''
                    },
                },
                object_story_spec: {
                    template_data: {
                        call_to_action: {
                            type: 'SHOP_NOW',
                        },
                        message: '',
                        name: '',
                        description: '',
                        link: '',
                        image_hash: '',
                        video_id: ''
                    },
                    page_id: adFormik.values?.page_id,
                    instagram_user_id: adFormik.values?.instagram_user_id,
                },
            },
            override_catalog: '',
            force_single_link: true,
            enroll_status: adFormik.values.enroll_status ? adFormik.values.enroll_status : 'OPT_OUT',
            picture: '',
            image_layer_specs: [],
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values, { setSubmitting }) => {

            const storySpec = {
                page_id: values.creative.object_story_spec.page_id,
                instagram_user_id: values.creative.object_story_spec.instagram_user_id
            };

            const contextual_multi_ads = {
                enroll_status: values?.enroll_status
            };

            // const degrees_of_freedom_spec = {
            //     creative_features_spec: {
            //         standard_enhancements: {
            //             enroll_status: "OPT_OUT"
            //         }
            //     }
            // };

            const filtered_image_layer_specs = values?.image_layer_specs?.map(layer => {
                // Create a shallow copy of the object without frame_name and frame_url
                const { frame_name, frame_url, ...rest } = layer;
                return rest;
            }) || [];

            // Construct the template_data
            const template_data = {
                ...(values.creative.object_story_spec.template_data.message?.trim() && {
                    message: values.creative.object_story_spec.template_data.message
                }),
                name: values.creative.object_story_spec.template_data.name,
                link: values.creative.object_story_spec.template_data.link,
                call_to_action: {
                    type: values.creative.object_story_spec.template_data.call_to_action.type,
                },
                force_single_link: true,
                ...(filtered_image_layer_specs.length > 0 && {
                    image_layer_specs: filtered_image_layer_specs,
                }),
            };

            // Construct ad creative object
            const adcreativePayload = {
                product_set_id: selectedAdset?.promoted_object?.product_set_id,
                object_type: "SHARE",
                ...(values.creative?.url_tags?.trim() && { url_tags: values.creative.url_tags }),
                applink_treatment: values?.creative?.applink_treatment,
                object_story_spec: {
                    template_data,
                    ...storySpec
                },
                template_url_spec: values.creative.template_url_spec,
                contextual_multi_ads,
                // degrees_of_freedom_spec
            };

            const initialPayload = {
                adcreative: adcreativePayload,
                name: values.name,
                cpa: Number(values.cpa),
                adset_id: selectedAdset?.adset_id,
                destination_type: selectedAdset?.destination_type,
                currency: adsAccountData?.currency,
                adsAccount: {
                    //id: adsAccountData?.id,
                    adsAccountId: adsAccountData?.account_id,
                    adsAccountName: adsAccountData?.name,
                    businessName: adsAccountData?.business?.name,
                    businessId: adsAccountData?.business?.id,
                },
            };

            const payload = removeEmptyValues(initialPayload);

            //  console.log({ payload });


            createAd({ payload })
                .unwrap()
                .then(response => {
                    onSuccess();
                    toast({
                        position: "top-right",
                        title: "Ad Created",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .catch(error => {
                    toast({
                        position: "top-right",
                        title: "Error creating Ad",
                        description: error?.data?.message,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setSubmitting(false);
                    onClose();
                });
        },
    });

    useEffect(() => {
        if (adFormik.values !== prevAdFormikValues) {
            setPrevAdFormikValues(adFormik.values);

            formik.setValues((prevValues) => ({
                ...prevValues,
                name: adFormik.values?.name,
                enroll_status: adFormik.values?.enroll_status,
                creative: {
                    ...prevValues.creative,
                    object_story_spec: {
                        ...prevValues.creative?.object_story_spec,
                        page_id: adFormik.values?.page_id,
                        instagram_user_id: adFormik.values?.instagram_user_id,
                    },
                },
            }));
        }
    }, [adFormik.values]);


    useEffect(() => {
        onFormValuesChange(formik.values);
    }, [formik.values, onFormValuesChange]);


    const handleUrlChange = (constructedUrl) => {
        formik.setFieldValue('creative.object_story_spec.template_data.link', constructedUrl);
    };

    const handleUrlParamChange = (constructedUrl) => {
        formik.setFieldValue('creative.url_tags', constructedUrl);
    };

    return (
        <Box direction={'column'} gap={4} width={'100%'}>
            <form onSubmit={formik.handleSubmit}>
                <Flex direction={'column'} gap={'10px'} borderRadius="md" bg={background} p={4} mb={4}>
                    <Flex direction="column" gap={1}>
                        <Flex alignItems="center">
                            <Text fontWeight="bold" >Destination</Text>
                        </Flex>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize={'14px'}>Default destination</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                        <Text>
                                            Send people to either your app or website URL.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md">
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Select
                            name="creative.applink_treatment"
                            bg="#fff"
                            size="sm"
                            borderRadius="md"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.creative.applink_treatment}
                        >
                            <option value="web_only">Website</option>
                            <option value="deeplink_with_web_fallback">App</option>
                        </Select>
                    </Flex>

                    {formik.values.creative.applink_treatment === 'deeplink_with_web_fallback' ?
                        <>
                            <Flex direction={'column'} gap={1} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize={'14px'}>Mobile app</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p="5px">
                                                <Text>Only select a mobile app if you are using a universal link (iOS or Android app link).</Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <Select
                                    name={`creative.template_url_spec.config.app_id`}
                                    bg="#fff"
                                    size="sm"
                                    borderRadius="md"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik?.values?.creative?.template_url_spec?.config?.app_id}
                                >
                                    <option value="">Choose app</option>
                                    {advertisableApps?.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </Select>
                            </Flex>

                            <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">iOS deep link</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Use a deep link to specify where in your app to send people. Entering an iOS deep link here will override any iOS deep links for items in the selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={formik.values?.creative?.template_url_spec?.ios?.url}
                                    onChange={(url) => {
                                        formik.setFieldValue('creative.template_url_spec.ios.url', url)
                                    }}
                                    error={
                                        formik.errors?.creative?.template_url_spec?.ios?.url
                                    }
                                    placeholder={'Enter the deep link URL'}
                                    maxW={'400px'}
                                />
                            </Flex>

                            <Flex direction={'column'} gap={2} mt={'10px'}>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Android deep link</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Use a deep link to specify where in your app to send people. Entering an Android deep link here will override any Android deep links for items in the selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                <CustomDeepLinkInput
                                    value={formik.values?.creative?.template_url_spec?.android?.url}
                                    onChange={(url) => formik.setFieldValue('creative.template_url_spec.android.url', url)}
                                    error={
                                        formik.errors?.creative?.template_url_spec?.android?.url
                                    }
                                    placeholder={'Enter the deep link URL'}
                                    maxW={'400px'}
                                />
                            </Flex>
                        </>
                        : null}

                    <Flex direction="column" gap={1} mt={'10px'}>
                        <Flex alignItems="center">
                            <Text fontWeight="bold" fontSize={'14px'}>Website URL</Text>
                            <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                        <Text>Enter the URL for the webpage you want people to visit</Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Input
                            name="creative.object_story_spec.template_data.link"
                            placeholder="Enter a URL"
                            bg="#fff"
                            size='sm'
                            borderRadius="md"
                            value={formik.values.creative.object_story_spec.template_data.link}
                            onChange={formik.handleChange}
                        />
                        <Flex mt={2}>
                            <Button onClick={() => {
                                setUrlModalOpen(true)
                            }}
                                variant="link"
                                color='brand'>
                                <Text fontSize={'12px'} textDecoration={'underline'}>{'Build a URL parameter'}</Text>
                            </Button>
                        </Flex>
                        {formik.touched.creative?.object_story_spec?.template_data?.link && formik.errors.creative?.object_story_spec?.template_data?.link && (
                            <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.object_story_spec?.template_data?.link}</Text>
                        )}
                    </Flex>

                    <Flex direction="column" mb={2} borderRadius="md" gap={2} mt={'10px'}>
                        <Checkbox
                            onChange={(e) => {
                                formik.setFieldValue("override_catalog", e.target.checked);
                            }}
                            colorScheme="brand"
                            isChecked={formik.values.override_catalog}
                            alignItems="flex-start"
                        >
                            <Flex direction="column">
                                <Flex align="center" mt={'-3px'}>
                                    <Text fontWeight="bold" fontSize="14px">
                                        Override catalog website deep links&nbsp;
                                    </Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p="5px">
                                                <Text>
                                                    Link each item in your catalog to a specific page on your website.
                                                    You can add deep links in your catalog's data feed. Use a deep link
                                                    to specify where in your website to send people. Entering a deep link
                                                    to website here will override any website deep links for items in the
                                                    selected catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>
                                {/* Description text on the next line */}
                                <Text fontSize="11px">
                                    This allows you to override any website deep links for items in the selected catalog.
                                </Text>
                            </Flex>
                        </Checkbox>
                        {formik.values.override_catalog ?
                            <>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontWeight="bold" fontSize="14px">Set catalog deep links</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="14px" p="5px">
                                                <Text>
                                                    Enter the URL for the website deep link you want people to visit. This URL will override the destination links in your catalog.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" />
                                    </Tooltip>
                                </Flex>

                                <CustomDeepLinkInput
                                    value={formik.values?.creative?.template_url_spec?.web?.url}
                                    onChange={(url) => formik.setFieldValue('creative.template_url_spec.web.url', url)}
                                    error={
                                        formik.touched?.creative?.template_url_spec?.web?.url &&
                                        formik.errors?.creative?.template_url_spec?.web?.url
                                    }
                                    placeholder={'http://www.example.com/page'}
                                    maxW={'400px'}
                                />

                            </>
                            : null}
                    </Flex>
                </Flex>

                <Flex direction={'column'} gap={'10px'} borderRadius="md" bg={background} p={4} mb={4}>
                    <Text fontWeight={'bold'}>Ad creative</Text>
                    <Text fontSize={'12px'}>Select and optimize your ad text, media and enhancements.</Text>

                    <Flex align="center" wrap="wrap" gap={2}>
                        <Text fontWeight="bold" fontSize={'14px'}>Creative tools</Text>
                        <Tooltip
                            borderRadius={5}
                            label={
                            <Box fontSize="sm" p="5px">
                                <Text>
                                Customise how images from your catalogue appear in your ads. Frame products and services with engaging graphics and add helpful info from your catalogue, such as price or free delivery.
                                </Text>
                            </Box>
                            }
                            fontSize="md"
                        >
                            <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                    </Flex>
                    
                    <Flex>
                        <Menu>
                        <MenuButton
                          as={Button}
                          variant="outline"
                          fontWeight="normal"
                          bg={inputBackground}
                          _active={{ background: inputBackground, borderColor: "blue.500" }}
                          _hover={{ background: inputBackground, borderColor: "gray.300" }}
                          rightIcon={<ChevronDownIcon boxSize={5} />}
                          size="sm"
                        >
                          Edit creative
                        </MenuButton>
                        <MenuList minW="135px" px={2} py={1}>
                          <MenuItem
                            fontSize="14px"
                            onClick={onCreativeModalOpen}
                          >
                            Add a frame
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>

                    <Flex direction="column" gap={1}>
                        <Flex alignItems="center">
                            <Text fontWeight="bold" fontSize="14px" mr={2}>Primary text</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="14px" p="5px">
                                        <Text>
                                            The primary text for your ad appears in most placements, though the position
                                            varies. You can use @ to tag Facebook profiles or Pages. We recommend 125
                                            characters or less.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>

                        <CustomDeepLinkInput
                            value={formik.values.creative.object_story_spec.template_data.message}
                            onChange={(message) => formik.setFieldValue('creative.object_story_spec.template_data.message', message)}
                            error={
                                formik.touched.creative?.object_story_spec?.template_data?.message &&
                                formik.errors.creative?.object_story_spec?.template_data?.message
                            }
                            placeholder={'Tell people what your ad is about'}
                            maxW={'400px'}
                        />
                    </Flex>

                    <Flex direction="column" gap={1} mt={'10px'}>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize={'14px'}>Headline</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                        <Text>
                                            Add up to five brief headlines to let people know what your ad is about. Each
                                            headline can have a maximum of 255 characters. Headlines won't appear in all
                                            placements.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md">
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>

                        <CustomDeepLinkInput
                            value={formik.values.creative.object_story_spec.template_data.name}
                            onChange={(name) => formik.setFieldValue('creative.object_story_spec.template_data.name', name)}
                            error={
                                formik.touched.creative?.object_story_spec?.template_data?.name &&
                                formik.errors.creative?.object_story_spec?.template_data?.name
                            }
                            placeholder={'Add an attention-grabbing headline'}
                            maxW={'400px'}
                        />
                    </Flex>

                    <Flex direction="column" gap={1} mt={'10px'}>
                        <Flex alignItems="center" mb={2} gap={2}>
                            <Text fontWeight="bold" fontSize="14px">Call to action</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                        <Text>Show a button or link on your ad that represents the action you want people to take.</Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>

                        <Select
                            name="creative.object_story_spec.template_data.call_to_action.type"
                            bg="#fff"
                            size="sm"
                            borderRadius="md"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.creative.object_story_spec.template_data.call_to_action.type}
                        >
                            {/* {call_to_action_options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))} */}
                            <option key={'SHOP_NOW'} value={'SHOP_NOW'}>
                                Shop Now
                            </option>
                        </Select>


                        {formik.touched.creative?.object_story_spec?.template_data?.call_to_action?.type &&
                            formik.errors.creative?.object_story_spec?.template_data?.call_to_action?.type && (
                                <Text fontSize="12px" color="red.500">
                                    {formik.errors.creative.object_story_spec.template_data.call_to_action.type}
                                </Text>
                            )}
                    </Flex>
                </Flex>

                {['APP', 'WHATSAPP', 'PHONE_CALL'].includes(selectedAdset?.destination_type) ? null :
                    <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                        <Text fontWeight={'bold'}>Tracking</Text>
                        <Flex direction="column" gap={1}>
                            <Flex alignItems={'center'}>
                                <Text mb={2} fontWeight="bold">
                                    URL parameters{" "}
                                    <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                                </Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Box fontSize="sm" p={'5px'}>
                                            <Text >
                                                Add parameters to the end of your website URL to track where your visitors are coming from.
                                            </Text>
                                        </Box>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                </Tooltip>
                            </Flex>
                            <Input
                                name="creative.url_tags"
                                placeholder="key1=value1&key2=value2"
                                bg="#fff"
                                size='sm'
                                borderRadius="md"
                                value={formik.values.creative.url_tags}
                                onChange={formik.handleChange}
                            />
                            <Flex mt={2}>
                                <Button onClick={() => {
                                    setUrlParamModalOpen(true)
                                }}
                                    variant="link"
                                    color='brand'>
                                    <Text fontSize={'12px'} textDecoration={'underline'}>{'Build a URL parameter'}</Text>
                                </Button>
                            </Flex>
                            {formik.touched.creative?.url_tags && formik.errors.creative?.url_tags && (
                                <Text fontSize={"12px"} color="red.500">{formik.errors.creative?.url_tags}</Text>
                            )}
                        </Flex>
                    </Flex>
                }

                <Flex direction={'column'} gap={2} borderRadius="md" bg={background} p={4} mb={4}>
                    <Flex direction="column" gap={1}>
                        <Text fontWeight="bold">CPA</Text>
                        <Input
                            name="cpa"
                            placeholder="Enter CPA"
                            bg="#fff"
                            size='sm'
                            borderRadius="md"
                            value={formik.values.cpa}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />
                        {formik.touched.cpa && formik.errors.cpa && (
                            <Text fontSize={"12px"} color="red.500">{formik.errors.cpa}</Text>
                        )}
                    </Flex>
                </Flex>

                <Flex mb={4}>
                    <Button
                        size="sm"
                        colorScheme="brand"
                        isLoading={formik.isSubmitting}
                        type="submit"
                        onClick={onSave}
                    >
                        Save
                    </Button>
                </Flex>
            </form>

            {/* Modal for "Add a frame" */}
            {isCreativeModalOpen && 
                <CatalogueFrameForSingle 
                    isOpen={isCreativeModalOpen}
                    onClose={onCreativeModalClose}
                    productSetId={selectedAdset?.promoted_object?.product_set_id}
                    selectedAdAccounts={[{
                        id: adsAccountData?.id,
                        adsAccountId: adsAccountData?.account_id,
                        adsAccountName: adsAccountData?.name,
                        businessName: adsAccountData?.business?.name,
                        businessId: adsAccountData?.business?.id
                    }]}
                    values={formik.values}
                    onChangeImageLayerSpecs={(framesData) => formik.setFieldValue("image_layer_specs", framesData)}
                />
            }

            <Modal isOpen={isUrlModalOpen} onClose={() => setUrlModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BuildUrlParamsUpdate link={formik.values.creative.object_story_spec.template_data} onClose={() => setUrlModalOpen(false)} onUrlChange={handleUrlChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Modal isOpen={isUrlParamModalOpen} onClose={() => setUrlParamModalOpen(false)} scrollBehavior={"inside"}
                size={"2xl"}
                isCentered={true}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <UrlParametersUpdate link={formik.values.creative} onClose={() => setUrlParamModalOpen(false)} onUrlChange={handleUrlParamChange} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default CreateSingleCatalogueAdForm;